/**
 * DictationSetupModal — first-run + reconfigure UX for the mic button.
 *
 * Two responsibilities:
 *
 *  1. Drive the one-time install of the offline-ASR model bundle on the
 *     backend, with a phase label + progress bar so the user doesn't
 *     think the app is stuck during the ~190 MB download.
 *  2. Let the user pick which input device to use. The chosen
 *     ``deviceId`` is persisted by the caller (see ChatInput) and
 *     passed through to ``useDictation`` on subsequent presses.
 *
 * The modal is intentionally non-blocking: once a download starts the
 * user can close it and the install continues in the background. The
 * status polling resumes the next time the modal is opened.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  fetchAudioStatus,
  installAudioEngine,
  type EngineStatus,
  type AudioPhase,
} from "@/api/audio";

interface Props {
  open: boolean;
  /** Currently saved deviceId — pre-selects the matching <option>. */
  deviceId: string | null;
  /** Called when the user picks a device (or "default"). */
  onDeviceChange: (deviceId: string | null) => void;
  onClose: () => void;
  /** Called when the user clicks "Done" after the model is ready. */
  onReady: () => void;
}

const PHASE_LABEL: Record<AudioPhase, string> = {
  idle: "Speech recognition model not installed",
  downloading: "Downloading offline speech recognition model",
  extracting: "Extracting model files",
  initializing: "Initializing recognizer",
  ready: "Speech recognition ready",
  error: "Install failed",
};

function fmtMB(bytes: number): string {
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function DictationSetupModal({
  open,
  deviceId,
  onDeviceChange,
  onClose,
  onReady,
}: Props) {
  const [status, setStatus] = useState<EngineStatus | null>(null);
  const [backendAvailable, setBackendAvailable] = useState<boolean | null>(null);
  const [backendError, setBackendError] = useState<string | null>(null);
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  const [permState, setPermState] = useState<"unknown" | "granted" | "denied">("unknown");
  const [installError, setInstallError] = useState<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  /** Stop polling — called on close + on phase transition to terminal. */
  const stopPoll = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const refreshStatus = useCallback(async () => {
    try {
      const s = await fetchAudioStatus();
      setStatus(s.asr);
      setBackendAvailable(s.backend_available);
      setBackendError(s.backend_error);
      if (s.asr.phase === "ready" || s.asr.phase === "error") {
        stopPoll();
      }
    } catch (e) {
      // Network errors during polling shouldn't blow up the modal —
      // just surface and keep trying on the next tick.
      console.warn("audio status poll failed", e);
    }
  }, [stopPoll]);

  // Refresh status + device list whenever the modal opens. We don't
  // poll while closed because the user can't see the progress anyway —
  // they'll get a fresh snapshot when they re-open the modal.
  useEffect(() => {
    if (!open) {
      stopPoll();
      return;
    }
    void refreshStatus();
    pollRef.current = setInterval(refreshStatus, 1000);

    // Enumerate input devices. Browsers withhold device **labels** until
    // we've been granted mic permission at least once — so we kick a
    // probing getUserMedia call when labels are empty. The mic indicator
    // briefly flashes but no audio is captured (we stop the tracks
    // immediately).
    const loadDevices = async () => {
      try {
        const list = await navigator.mediaDevices.enumerateDevices();
        const audioIn = list.filter((d) => d.kind === "audioinput");
        const hasLabels = audioIn.some((d) => d.label);
        if (!hasLabels) {
          try {
            const probe = await navigator.mediaDevices.getUserMedia({ audio: true });
            probe.getTracks().forEach((t) => t.stop());
            setPermState("granted");
            const fresh = await navigator.mediaDevices.enumerateDevices();
            setDevices(fresh.filter((d) => d.kind === "audioinput"));
          } catch {
            setPermState("denied");
            setDevices(audioIn);
          }
        } else {
          setPermState("granted");
          setDevices(audioIn);
        }
      } catch (e) {
        console.warn("enumerateDevices failed", e);
      }
    };
    void loadDevices();

    return () => {
      stopPoll();
    };
  }, [open, refreshStatus, stopPoll]);

  const handleInstall = useCallback(async () => {
    setInstallError(null);
    try {
      await installAudioEngine("asr");
      // Bump the poll immediately so the modal flips to "Downloading…"
      // without waiting a second for the next tick.
      await refreshStatus();
      if (!pollRef.current) pollRef.current = setInterval(refreshStatus, 1000);
    } catch (e) {
      setInstallError(e instanceof Error ? e.message : "install failed");
    }
  }, [refreshStatus]);

  // ``installed`` reflects bundle presence on disk — the source of truth
  // for "can we start dictation?". Phase tracks the live install state
  // and is orthogonal: e.g. after a backend restart, ``installed=true``
  // but ``phase="ready"`` only after the status endpoint promotes idle→ready.
  const installed = status?.installed === true;
  const inProgress =
    status?.phase === "downloading" ||
    status?.phase === "extracting" ||
    status?.phase === "initializing";
  const backendMissing = backendAvailable === false;

  const percent = useMemo(() => {
    if (!status || !status.total) return null;
    return Math.min(100, Math.round((status.bytes / status.total) * 100));
  }, [status]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-space/40 backdrop-blur-sm">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="dictation-setup-title"
        className="bg-surface rounded-2xl shadow-xl border border-space-30 w-full max-w-md mx-4 p-5"
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 id="dictation-setup-title" className="text-base font-semibold text-space">
              Dictation setup
            </h2>
            <p className="text-xs text-space-50 mt-0.5">
              Runs locally on this machine — audio never leaves your computer.
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-space-40 hover:text-space p-1 -m-1"
            aria-label="Close"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* ── Model status ────────────────────────────────────────── */}
        <section className="mb-5">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-space-50 mb-2">
            Speech recognition model
          </h3>

          <div className="rounded-lg border border-space-30 p-3 bg-space-20/30">
            <div className="flex items-center gap-2">
              <span
                className={`inline-block h-2 w-2 rounded-full shrink-0 ${
                  backendMissing
                    ? "bg-hydra"
                    : installed
                      ? "bg-emerald-500"
                      : status?.phase === "error"
                        ? "bg-hydra"
                        : inProgress
                          ? "bg-jove animate-pulse"
                          : "bg-space-40"
                }`}
              />
              <p className="text-sm text-space flex-1">
                {backendMissing
                  ? "Backend dependency missing"
                  : status
                    ? PHASE_LABEL[status.phase]
                    : "Checking…"}
              </p>
              {!backendMissing && !installed && !inProgress && (
                <button
                  onClick={handleInstall}
                  className="px-3 py-1 rounded-lg bg-primary text-on-primary text-xs font-medium hover:bg-jove-200 transition-colors"
                >
                  Install (~190 MB)
                </button>
              )}
            </div>

            {inProgress && (
              <div className="mt-3">
                <div className="h-1.5 bg-space-30 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-jove transition-all duration-300"
                    style={{
                      width: percent !== null ? `${percent}%` : "30%",
                      // When total is unknown (no Content-Length) the bar
                      // shows an indeterminate-ish stripe at 30 %.
                    }}
                  />
                </div>
                <p className="text-[11px] text-space-50 mt-1.5 tabular-nums">
                  {status?.phase === "downloading" && status.total > 0
                    ? `${fmtMB(status.bytes)} / ${fmtMB(status.total)}${
                        percent !== null ? ` (${percent}%)` : ""
                      }`
                    : status?.phase === "downloading"
                      ? `${fmtMB(status.bytes)} downloaded`
                      : status?.phase === "extracting"
                        ? "Extracting…"
                        : "Initializing recognizer…"}
                </p>
              </div>
            )}

            {(installError || status?.error) && (
              <p className="text-xs text-hydra mt-2 break-words">
                {installError ?? status?.error}
              </p>
            )}

            {backendMissing && (
              <div className="mt-3 space-y-2">
                <p className="text-xs text-space-50 break-words">
                  The <code className="font-mono bg-space-30/50 px-1 rounded">sherpa-onnx</code>{" "}
                  Python package isn't installed in this backend's environment, so the
                  recognizer can't be loaded.
                </p>
                <p className="text-xs text-space-50">
                  In a dev venv, refresh dependencies and restart the backend:
                </p>
                <pre className="text-[11px] font-mono bg-space-30/30 text-space rounded px-2 py-1 overflow-x-auto">
                  uv pip install -e &quot;.[desktop]&quot;
                </pre>
                {backendError && (
                  <p className="text-[11px] text-space-40 break-words" title={backendError}>
                    Import error: {backendError}
                  </p>
                )}
              </div>
            )}
          </div>
        </section>

        {/* ── Device picker ──────────────────────────────────────── */}
        <section className="mb-5">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-space-50 mb-2">
            Microphone
          </h3>

          {permState === "denied" ? (
            <p className="text-xs text-hydra">
              Microphone permission was denied. Enable it in your browser / OS
              settings and reopen this dialog to pick a device.
            </p>
          ) : (
            <select
              value={deviceId ?? ""}
              onChange={(e) => onDeviceChange(e.target.value || null)}
              className="w-full px-3 py-2 rounded-lg border border-space-30 bg-surface text-sm
                         focus:outline-none focus:border-jove-60 focus:ring-2 focus:ring-jove-60/20"
            >
              <option value="">System default</option>
              {devices.map((d) => (
                <option key={d.deviceId} value={d.deviceId}>
                  {d.label || `Microphone (${d.deviceId.slice(0, 8)}…)`}
                </option>
              ))}
            </select>
          )}
          {permState === "granted" && devices.length === 0 && (
            <p className="text-[11px] text-space-50 mt-1.5">
              No microphones detected.
            </p>
          )}
        </section>

        {/* ── Footer actions ─────────────────────────────────────── */}
        <div className="flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            className="px-3 py-1.5 rounded-lg text-sm text-space-50 hover:bg-space-20 transition-colors"
          >
            Close
          </button>
          <button
            onClick={onReady}
            disabled={!installed || backendMissing}
            className="px-3 py-1.5 rounded-lg text-sm font-medium bg-primary text-on-primary hover:bg-jove-200
                       disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
